import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/data-table/DataTable';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { formatDateTime } from '@/lib/utils/date';
import { useDeleteWhitelist, useWhitelist } from '../api';
import type { WhitelistRow } from '../types';

export function WhitelistListPage() {
  const list = useWhitelist();
  const rows = list.data ?? [];
  const del = useDeleteWhitelist();
  const [pendingDelete, setPendingDelete] = useState<WhitelistRow | null>(null);

  const columns = useMemo<ColumnDef<WhitelistRow>[]>(
    () => [
      {
        accessorKey: 'whitelistId',
        header: 'ID',
        size: 80,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {row.original.whitelistId}
          </span>
        ),
      },
      {
        accessorKey: 'mobile',
        header: 'Mobile',
        cell: ({ row }) => (
          <span className="font-mono tabular-nums">{row.original.mobile}</span>
        ),
      },
      {
        accessorKey: 'reqDate',
        header: 'Added on',
        size: 200,
        cell: ({ row }) =>
          row.original.reqDate ? formatDateTime(row.original.reqDate) : '—',
      },
      {
        id: 'actions',
        header: '',
        size: 60,
        enableSorting: false,
        enableHiding: false,
        cell: ({ row }) => (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-destructive hover:text-destructive"
            onClick={() => setPendingDelete(row.original)}
            aria-label="Remove from whitelist"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        ),
      },
    ],
    [],
  );

  return (
    <FillPage>
      <PageHeader
        title="Whitelist"
        description="Numbers explicitly allowed to bypass the platform's DND / blacklist guard."
        actions={
          <Button asChild>
            <Link to="/add-whitelist">
              <Plus className="h-4 w-4" />
              Add to whitelist
            </Link>
          </Button>
        }
      />

      <DataTable<WhitelistRow, unknown>
        tableId="number-actions:whitelist:v1"
        columns={columns}
        data={rows}
        isLoading={list.isLoading}
        searchPlaceholder="Search by mobile…"
        emptyTitle="No whitelisted numbers"
        emptyDescription='Click "Add to whitelist" to add your first entry.'
        getRowId={(row) => String(row.whitelistId)}
        initialColumnPinning={{ right: ['actions'] }}
        exportFilename="whitelist.csv"
        pageSize={25}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Remove from whitelist?"
        description={
          <>
            Mobile{' '}
            <span className="font-mono font-medium text-foreground">
              {pendingDelete?.mobile}
            </span>{' '}
            will no longer bypass DND / blacklist checks.
          </>
        }
        confirmLabel="Remove"
        tone="destructive"
        loading={del.isPending}
        onConfirm={() => {
          if (!pendingDelete) return;
          del.mutate(pendingDelete.mobile, {
            onSuccess: (res) => {
              toast.success(res?.message ?? 'Removed from whitelist');
              setPendingDelete(null);
            },
            onError: () => toast.error('Could not remove from whitelist.'),
          });
        }}
      />
    </FillPage>
  );
}
